import { endpoints } from "../Api/configs";
import apiClient from "./apiClient";

/**
 * Activity Types for user activity tracking
 */
export const ACTIVITY_TYPES = {
    EVENT_CREATED: "event_created",
    CHALLENGE_SENT: "challenge_sent",
    CHALLENGE_SHARED_SOCIAL: "challenge_shared_social",
    CHALLENGE_ACCEPTED: "challenge_accepted",
    CHALLENGE_REJECTED: "challenge_rejected",
    CHALLENGE_JOINED_VIA_INVITE: "challenge_joined_via_invite",
    NEGOTIATION_STARTED: "negotiation_started",
    TERMS_MODIFIED: "terms_modified",
    OPPONENT_CHANGED: "opponent_changed",
    BELTS_PURCHASED: "belts_purchased",
    JOINED_LOBBY: "joined_lobby",
    CONFIRMED_ATTENDANCE: "confirmed_attendance",
    PLATFORM_CONNECTED: "platform_connected",
    BROADCASTER_DOWNLOADED: "broadcaster_downloaded",
    CHECKED_IN: "checked_in",
    ENTERED_STUDIO: "entered_studio",
    STREAM_STARTED: "stream_started",
    STREAM_ENDED: "stream_ended",
    CHAT_MESSAGE_SENT: "chat_message_sent",
    EVENT_COMPLETED: "event_completed",
    EVENT_CANCELLED: "event_cancelled",
} as const;

export type ActivityType = typeof ACTIVITY_TYPES[keyof typeof ACTIVITY_TYPES];

/**
 * Interface for activity metadata
 */
export interface ActivityMetadata {
    platform?: string; // For platform_connected
    [key: string]: any;
}

/**
 * Interface for stored activity
 */
export interface UserActivity {
    activityId: string;
    eventId: string;
    userId: string;
    userName: string;
    userProfilePicture: string | null;
    activityType: ActivityType;
    createdAt: string;
    metadata: ActivityMetadata | null;
}

/**
 * Store a user activity for an event
 * 
 * @param accessToken - User's access token
 * @param eventId - Event ID
 * @param activityType - Type of activity (use ACTIVITY_TYPES constants)
 * @param metadata - Optional additional data
 * @returns Promise with the stored activity result
 * 
 * @example
 * // Simple usage
 * await storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.CHECKED_IN);
 * 
 * // With metadata
 * await storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.PLATFORM_CONNECTED, { platform: 'youtube' });
 */
export const storeUserActivity = async (
    accessToken: string,
    eventId: string,
    activityType: ActivityType,
    metadata?: ActivityMetadata
): Promise<{
    success: boolean;
    activityId?: string;
    createdAt?: string;
    error?: string;
}> => {
    try {
        const response = await apiClient(endpoints.activity.storeActivity, {
            method: "POST",
            body: JSON.stringify({
                accessToken,
                eventId,
                activityType,
                ...(metadata && { metadata }),
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });

        if (response.statusCode === 201) {
            return {
                success: true,
                activityId: response.body?.activityId,
                createdAt: response.body?.createdAt,
            };
        } else {
            return {
                success: false,
                error: response.body?.error || "Failed to store activity",
            };
        }
    } catch (error: any) {
        console.error("Error storing user activity:", error);
        return {
            success: false,
            error: error.message || "Failed to store activity",
        };
    }
};

/**
 * Get all activities for an event
 * 
 * @param accessToken - User's access token
 * @param eventId - Event ID
 * @returns Promise with activities array
 * 
 * @example
 * const { activities } = await getEventActivities(accessToken, eventId);
 */
export const getEventActivities = async (
    accessToken: string,
    eventId: string
): Promise<{
    success: boolean;
    activities?: UserActivity[];
    totalCount?: number;
    error?: string;
}> => {
    try {
        const url = `${endpoints.activity.getActivities}?accessToken=${encodeURIComponent(
            accessToken
        )}&eventId=${encodeURIComponent(eventId)}`;

        const response = await apiClient(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        // Response is already parsed - check if eventId exists (success case)
        // or if there's an error field (error case)
        if (response.eventId && response.activities) {
            return {
                success: true,
                activities: response.activities || [],
                totalCount: response.totalCount || 0,
            };
        } else if (response.statusCode === 200 && response.body?.activities) {
            // Fallback for statusCode wrapper format
            return {
                success: true,
                activities: response.body.activities || [],
                totalCount: response.body.totalCount || 0,
            };
        } else {
            return {
                success: false,
                error: response.error || response.body?.error || "Failed to fetch activities",
            };
        }
    } catch (error: any) {
        console.error("Error fetching event activities:", error);
        return {
            success: false,
            error: error.message || "Failed to fetch activities",
        };
    }
};

/**
 * Helper function to track check-in activity
 */
export const trackCheckIn = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.CHECKED_IN);
};

/**
 * Helper function to track platform connection
 */
export const trackPlatformConnected = (
    accessToken: string,
    eventId: string,
    platform: "youtube" | "twitch"
) => {
    return storeUserActivity(
        accessToken,
        eventId,
        ACTIVITY_TYPES.PLATFORM_CONNECTED,
        { platform }
    );
};

/**
 * Helper function to track entering studio
 */
export const trackEnteredStudio = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.ENTERED_STUDIO);
};

/**
 * Helper function to track stream started
 */
export const trackStreamStarted = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.STREAM_STARTED);
};

/**
 * Helper function to track confirmed attendance
 */
export const trackConfirmedAttendance = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.CONFIRMED_ATTENDANCE);
};

/**
 * Helper function to track broadcaster downloaded
 */
export const trackBroadcasterDownloaded = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.BROADCASTER_DOWNLOADED);
};

/**
 * Helper function to track joined via invite
 */
export const trackChallengeJoinedViaInvite = (accessToken: string, eventId: string) => {
    return storeUserActivity(accessToken, eventId, ACTIVITY_TYPES.CHALLENGE_JOINED_VIA_INVITE);
};
